import Sortable from "sortablejs";
import { applySortableReorder, createReorderable } from "../shared/sortable-reorder";
import { parseJsonOr } from "../shared/utils";
import { externalHref, normalizeExternalUrl } from "./normalize-url";

// One canonical surrounding place, projected from the property's
// shared/properties/<slug>.json#property.surroundings array. `icon` is the
// canonical icon that rides along in the pool (used as the editor-preview
// fallback when no per-id override exists). `fallbackIcon` is the category
// default computed server-side (from the shared surroundings-category-icons
// map) and injected only for category-only places with no canonical icon, so
// the editor preview matches the live page instead of showing 'map-pin'.
interface SurroundingPlace {
  id: string;
  name: string;
  category?: string;
  distance?: string;
  icon?: string;
  fallbackIcon?: string;
}

interface CMSSurroundingPickerConfig {
  field: string;
  // Autosave routing key — threaded into the cms-image-changed detail so the
  // cmsEditor controller routes the write to property:sections.location.
  target: string;
  // Raw JSON strings from the widget ctx (parsed here so the templ never has to
  // build nested Alpine literals): pool = SurroundingPlace[], icons = {id:icon},
  // descriptions = {id:text}, mapHidden = {id:bool}.
  pool: string;
  icons: string;
  descriptions: string;
  urls: string;
  mapHidden: string;
  // category enum → translated label, built server-side from the same
  // websites.location.attraction.type.* i18n keys the hub type dropdown uses
  // (the pool carries the raw enum value; the picker can't call Go t()).
  categoryLabels: string;
  // Ordered curated id list (featured_surrounding_ids).
  selected: string[];
}

// Hardcoded selection bounds — NOT in variant-constraints (the live location
// section renders any count; these are an editorial guardrail only, advisory).
const MIN = 2;
const MAX = 8;

export function cmsSurroundingPicker(config: CMSSurroundingPickerConfig) {
  const pool = parseJsonOr(config.pool, [] as SurroundingPlace[]);
  const icons = parseJsonOr(config.icons, {} as Record<string, string>);
  const descriptions = parseJsonOr(config.descriptions, {} as Record<string, string>);
  const urls = parseJsonOr(config.urls, {} as Record<string, string>);
  const mapHidden = parseJsonOr(config.mapHidden, {} as Record<string, boolean>);
  const categoryLabels = parseJsonOr(config.categoryLabels, {} as Record<string, string>);

  return {
    field: config.field,
    target: config.target,
    pool,
    icons,
    descriptions,
    urls,
    mapHidden,
    categoryLabels,
    selected: [...(config.selected || [])],
    search: "",
    MIN,
    MAX,
    _sortable: null as Sortable | null,

    init() {
      // SortableJS lives on the stable <ul x-ref="list">; Alpine's keyed x-for
      // only swaps the <li> children, so the instance survives add/remove.
      (this as any).$nextTick(() => this._initSortable());
    },

    destroy() {
      this._teardownSortable();
    },

    // The pool record for an id (name/category/distance/icon read-only display).
    byId(id: string): SurroundingPlace | undefined {
      return this.pool.find((p) => p.id === id);
    },

    // Translated label for a category enum value (food/transport/nature/...),
    // falling back to the raw value if unmapped. Labels come from the same
    // i18n keys the property-hub type dropdown uses.
    categoryLabel(cat?: string): string {
      return (cat && this.categoryLabels[cat]) || cat || "";
    },

    // Editor-preview icon: per-id override → canonical pool icon → category
    // fallback → 'map-pin'. The category→icon heuristic is NOT duplicated here;
    // it runs server-side (shared surroundings-category-icons map) and rides in
    // as `fallbackIcon`, so a category-only place (no operator pick) shows the
    // same icon the live page renders instead of the bare 'map-pin' placeholder.
    displayIcon(id: string): string {
      const p = this.byId(id);
      return this.icons[id] || p?.icon || p?.fallbackIcon || "map-pin";
    },

    // Per-place editorial description (the per-id override map the picker writes
    // alongside featured_surrounding_ids). Empty string when none — the live page
    // falls back to the canonical surrounding facts.
    displayDescription(id: string): string {
      return this.descriptions[id] || "";
    },

    // Pool items not yet selected, filtered by the search box (name/category).
    available(): SurroundingPlace[] {
      const q = this.search.trim().toLowerCase();
      return this.pool.filter((p) => {
        if (this.selected.includes(p.id)) return false;
        if (!q) return true;
        return (
          (p.name || "").toLowerCase().includes(q) || (p.category || "").toLowerCase().includes(q)
        );
      });
    },

    add(id: string) {
      if (this.selected.includes(id)) return;
      this.selected = [...this.selected, id];
      this.emit();
    },

    remove(id: string) {
      const i = this.selected.indexOf(id);
      if (i === -1) return;
      this.selected.splice(i, 1);
      // Drop now-orphaned overrides so the icons/descriptions/urls/mapHidden maps
      // stay in lockstep.
      delete this.icons[id];
      delete this.descriptions[id];
      delete this.urls[id];
      delete this.mapHidden[id];
      this.emit();
    },

    // Per-place icon override (from the shared icon-picker pick callback).
    setIcon(id: string, name: string) {
      this.icons[id] = name;
      this.emit();
    },

    // Per-place description override (from the textarea @input handler). An empty
    // value clears the override so the map stays in lockstep with the canonical
    // fallback (the live page renders the canonical facts when none is set).
    setDescription(id: string, val: string) {
      if (val) {
        this.descriptions[id] = val;
      } else {
        delete this.descriptions[id];
      }
      this.emit();
    },

    // Per-place editorial external link override (the per-id override map the
    // picker writes alongside featured_surrounding_ids). Empty string when none.
    displayUrl(id: string): string {
      return this.urls[id] || "";
    },

    // Per-place external link override. Empty clears it so the map stays in
    // lockstep with the canonical fallback (no link → plain card on the site).
    setUrl(id: string, val: string) {
      const v = val.trim();
      if (v) {
        this.urls[id] = v;
      } else {
        delete this.urls[id];
      }
      this.emit();
    },

    // normalizeUrl is the url input's @change (blur) handler: it auto-formats a
    // bare "example.com" / "www.example.com" into "https://…" (preserving the
    // host, never touching an existing scheme), then stores it via setUrl. The
    // input's :value="displayUrl(id)" binding reflects the corrected value back,
    // so what the operator sees is exactly what renders as a link. The live
    // @input keeps saving the raw value while typing; blur finalizes it.
    normalizeUrl(id: string, val: string) {
      this.setUrl(id, normalizeExternalUrl(val));
    },

    // previewHref backs the small "open link" affordance under the url input: a
    // safe http(s) href, or null when the value isn't a usable external link.
    previewHref(raw: string): string | null {
      return externalHref(raw);
    },

    // Per-place map-marker visibility override (from the "show on map" checkbox
    // handler — the templ inverts checked state to hidden before calling this).
    // false (shown, the default) clears the override so the map stays in
    // lockstep with the canonical default (every surrounding shows on the map
    // unless explicitly hidden).
    setMapHidden(id: string, hidden: boolean) {
      if (hidden) {
        this.mapHidden[id] = true;
      } else {
        delete this.mapHidden[id];
      }
      this.emit();
    },

    _initSortable() {
      const el = (this as any).$refs.list as HTMLElement | undefined;
      if (!el) return;
      this._teardownSortable();
      this._sortable = createReorderable(el, {
        handle: "[data-sort-handle]",
        draggable: "[data-sort-id]",
        // applySortableReorder undoes Sortable's DOM move THEN reorders the
        // array, so Alpine's keyed x-for doesn't move the node a second time
        // (the snap-back/desync bug). Mirrors the repeater + curation-modal.
        onEnd: (e: Sortable.SortableEvent) => {
          this.selected = applySortableReorder(el, e, this.selected, "data-sort-id");
          this.emit();
        },
      });
    },

    _teardownSortable() {
      if (this._sortable) {
        this._sortable.destroy();
        this._sortable = null;
      }
    },

    // emit fires FIVE whole-value events on $root so the cmsEditor controller
    // autosaves all keys into the SAME property:sections.location overlay:
    // featured_surrounding_ids (the ordered selection) + surroundings_icons (the
    // per-id icon override map) + surroundings_descriptions (the per-id text
    // override map) + surroundings_urls (the per-id external link override
    // map) + surroundings_map_hidden (the per-id show/hide-on-map override
    // map). All go via the structured cms-image-changed channel (detail.value
    // path, NOT the string input path).
    emit() {
      const root = (this as any).$root as HTMLElement;
      const fire = (f: string, v: unknown) =>
        root.dispatchEvent(
          new CustomEvent("cms-image-changed", {
            detail: { field: f, target: this.target, value: v },
            bubbles: true,
          }),
        );
      fire("featured_surrounding_ids", [...this.selected]);
      fire("surroundings_icons", { ...this.icons });
      fire("surroundings_descriptions", { ...this.descriptions });
      fire("surroundings_urls", { ...this.urls });
      fire("surroundings_map_hidden", { ...this.mapHidden });
    },
  };
}
